/**
 * 时间处理工具 - 统一使用北京时间
 */

/**
 * 获取当前北京时间（UTC+8）
 * @returns {Date} 北京时间Date对象
 */
export function getBeijingTime() {
  const now = new Date();
  // 获取UTC时间
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  // 转换为北京时间 (UTC+8)
  return new Date(utc + (3600000 * 8));
}

/**
 * 格式化时间为北京时间字符串 (YYYY-MM-DD HH:mm:ss)
 * @param {Date|string|number} date - 日期对象、时间字符串或时间戳
 * @returns {string} 格式化的北京时间字符串
 */
export function formatBeijingTime(date) {
  let beijingDate;
  
  if (date instanceof Date) {
    // 如果是Date对象，转换为北京时间
    const utc = date.getTime() + (date.getTimezoneOffset() * 60000);
    beijingDate = new Date(utc + (3600000 * 8));
  } else if (typeof date === 'string') {
    // 如果是字符串，先解析为Date对象
    const parsedDate = new Date(date);
    const utc = parsedDate.getTime() + (parsedDate.getTimezoneOffset() * 60000);
    beijingDate = new Date(utc + (3600000 * 8));
  } else if (typeof date === 'number') {
    // 如果是时间戳，直接转换为北京时间
    const timestampDate = new Date(date);
    const utc = timestampDate.getTime() + (timestampDate.getTimezoneOffset() * 60000);
    beijingDate = new Date(utc + (3600000 * 8));
  } else {
    // 默认使用当前北京时间
    beijingDate = getBeijingTime();
  }
  
  // 格式化为 YYYY-MM-DD HH:mm:ss
  const year = beijingDate.getFullYear();
  const month = String(beijingDate.getMonth() + 1).padStart(2, '0');
  const day = String(beijingDate.getDate()).padStart(2, '0');
  const hours = String(beijingDate.getHours()).padStart(2, '0');
  const minutes = String(beijingDate.getMinutes()).padStart(2, '0');
  const seconds = String(beijingDate.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * 获取当前北京时间的毫秒级时间戳
 * @returns {number} 毫秒级时间戳
 */
export function getBeijingTimestamp() {
  return getBeijingTime().getTime();
}

/**
 * 将服务器时间字符串转换为北京时间
 * 假设服务器存储的是UTC时间或已经是中国时间
 * @param {string} serverTime - 服务器时间字符串 (格式: YYYY-MM-DD HH:mm:ss)
 * @returns {string} 北京时间字符串
 */
export function convertServerTimeToBeijing(serverTime) {
  if (!serverTime) return '';
  
  // 如果服务器时间已经是北京时间格式，直接返回
  // 这里假设服务器存储的是北京时间
  return serverTime;
}

/**
 * 检查是否为有效的北京时间字符串
 * @param {string} timeStr - 时间字符串
 * @returns {boolean} 是否有效
 */
export function isValidBeijingTime(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return false;
  
  const pattern = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
  if (!pattern.test(timeStr)) return false;
  
  try {
    const [datePart, timePart] = timeStr.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, seconds] = timePart.split(':').map(Number);
    
    // 基本范围检查
    if (year < 2000 || year > 2100) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (hours < 0 || hours > 23) return false;
    if (minutes < 0 || minutes > 59) return false;
    if (seconds < 0 || seconds > 59) return false;
    
    return true;
  } catch (error) {
    return false;
  }
}

export default {
  getBeijingTime,
  formatBeijingTime,
  getBeijingTimestamp,
  convertServerTimeToBeijing,
  isValidBeijingTime
};